import { Component, computed, inject, Input, signal, WritableSignal } from '@angular/core';
import { FormGroup, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ResolutionService } from '../../../shared/resolution.service';
import { SignalsStoreService } from '../../../shared/signals-store.service';
import { CommonModule } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { InvoiceHistoryService } from './invoice-history.service';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator'
import { InvoiceHistory } from './invoice-history.types';
import { finalize, switchMap } from 'rxjs';
import { EmptyMessageComponent } from '../../../shared/empty-message/empty-message.component';
import { FileRequestService } from '../../../shared/file-request.service';

@Component({
    selector: 'app-invoice-history',
    providers: [InvoiceHistoryService],
    imports: [
        MatDatepickerModule,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        CommonModule,
        NgbModule,
        MatProgressBar,
        MatPaginatorModule,
        EmptyMessageComponent
    ],
    templateUrl: './invoice-history.component.html',
    styleUrl: './invoice-history.component.scss'
})
export class InvoiceHistoryComponent {
  private invoiceHistoryService = inject(InvoiceHistoryService);
  private resolutionsService = inject(ResolutionService);
  private signalsStoreService = inject(SignalsStoreService);
  private fileRequestService = inject(FileRequestService);

  invoicesLength = computed(() => this.invoiceHistoryService.invoicesLength());

  range = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });

  invoiceHistory = computed(() => this.invoiceHistoryService.invoiceHistory());
  credits: WritableSignal<number> = signal(0);

  isMobile = computed(() => this.resolutionsService.isMobile());

  isLimitedUser = computed(() => this.signalsStoreService.isLimitedUser());

  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions = [5, 10, 25];

  pageEvent!: PageEvent;

  /** Set by the past-due flow when this component is rendered inside the modal.
   *  It scopes the request to the past-due invoices, so the customer sees what
   *  they owe rather than the full history the settings route shows. */
  @Input() unpaidOnly = false;

  isLoading = computed(() => this.invoiceHistoryService.isLoading());

  // Heading and empty-state copy follow the context: the past-due flow reads as
  // "Pending Invoices", everything else keeps the full Invoice History wording.
  get heading(): string {
    return this.unpaidOnly ? 'Pending Invoices' : 'Invoice History';
  }

  get emptyMessage(): string {
    return this.unpaidOnly
      ? 'You have no outstanding invoices.'
      : 'You have no invoice history at this moment.';
  }

  handlePageEvent(e: PageEvent) {
    this.pageEvent = e;
    this.pageIndex = this.pageSize !== e.pageSize ? 0 : e.pageIndex;
    this.pageSize = e.pageSize;
    this.invoiceHistoryService.getInvoiceHistory(this.pageIndex, this.pageSize, this.unpaidOnly);
  }

  ngOnInit(): void {
    this.invoiceHistoryService.getInvoiceHistory(this.pageIndex, this.pageSize, this.unpaidOnly);
  }

  goToInvoice(invoice: InvoiceHistory) {
    // The tab is opened first, while the gesture is still live, and filled once the URL and
    // then the PDF have arrived — two round trips the pop-up blocker would otherwise outlast.
    const tab = this.fileRequestService.openPendingTab();
    invoice.isLoading = true;

    this.invoiceHistoryService.getInvoiceDetails(invoice.id).pipe(
      switchMap(response => this.fileRequestService.showInTab(tab, response?.data?.invoiceURL)),
      finalize(() => {
        invoice.isLoading = false;
        this.fileRequestService.closeIfPending(tab);
      })
    ).subscribe();
  }

}
